import clsx from 'clsx'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import { CitationBadge } from 'components/ui/CitationBadge'
import { ToolSourceRow } from 'components/messages/ToolSourceRow'
import { CitationSource } from 'types/chat-message'
import { getFileIcon } from 'utils/chat-attachments'
import { analytics, ANALYTICS_EVENTS } from 'services/analytics'
import { useOpenSource } from 'components/notebook/DocumentPreviewContext'

type SourceRowProps = {
  sourceKey: string
  source: CitationSource
}

const ROW_CLASSES = 'flex items-center gap-1.5 text-xs truncate'

export const SourceRow = ({ sourceKey, source }: SourceRowProps) => {
  // Attachment sources are keyed "att:N"; the badge shows only the number, like every other pill.
  const badgeLabel = sourceKey.split(':').pop() ?? sourceKey
  const openSource = useOpenSource()

  // Only the rows rendered as anchors below reach this: qa sources are never links.
  const trackClick = () => analytics.track(ANALYTICS_EVENTS.SOURCE_CLICKED, { source_type: source.type })

  if (source.type === 'qa') {
    return (
      <span className={clsx(ROW_CLASSES, 'text-muted-foreground')}>
        <CitationBadge label={badgeLabel} variant="neutral" size="sm" />
        <OdinIcon icon={['fas', 'comment-dots']} className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="truncate">{source.name}</span>
      </span>
    )
  }

  // qlik: a tool call with no document behind it expands to show its args (e.g. the Qlik
  // query) so the user can inspect/reproduce them.
  if (source.type === 'tool' && !source.url) {
    return <ToolSourceRow sourceKey={sourceKey} source={source} />
  }

  // Agentic tool call: show the tool name. Only tools that produced a document (e.g. `pdf`)
  // carry a url — a private upload linked to the chat, exchanged for a signed url on click.
  if (source.type === 'tool') {
    const content = (
      <>
        <CitationBadge label={badgeLabel} variant="neutral" size="sm" />
        <OdinIcon icon={['fas', source.url ? getFileIcon(source.url) : 'gear']} className="h-3 w-3 shrink-0" />
        <span className={clsx('truncate', !source.url && 'font-mono')}>{source.name}</span>
      </>
    )
    return source.url ? (
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.preventDefault()
          trackClick()
          void openSource(source)
        }}
        className={clsx(ROW_CLASSES, 'text-foreground hover:text-primary hover:underline')}
      >
        {content}
      </a>
    ) : (
      <span className={clsx(ROW_CLASSES, 'text-muted-foreground')}>{content}</span>
    )
  }

  if (source.type === 'attachment') {
    const content = (
      <>
        <CitationBadge label={badgeLabel} variant="neutral" size="sm" />
        <OdinIcon icon={['fas', getFileIcon(source.name)]} className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="truncate">{source.name}</span>
      </>
    )
    return source.url ? (
      <a
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => {
          event.preventDefault()
          trackClick()
          void openSource(source)
        }}
        className={clsx(ROW_CLASSES, 'text-foreground hover:text-primary hover:underline')}
      >
        {content}
      </a>
    ) : (
      <span className={clsx(ROW_CLASSES, 'text-muted-foreground')}>{content}</span>
    )
  }

  const isSite = source.type === 'site'
  const href = isSite
    ? (source.pageUrl ?? source.url ?? '')
    : source.url
      ? source.url + (source.page != null ? `#page=${source.page}` : '')
      : ''

  const content = (
    <>
      <CitationBadge label={badgeLabel} size="sm" />
      <OdinIcon icon={['fas', isSite ? 'globe' : getFileIcon(source.name)]} className="h-3 w-3 shrink-0 text-primary" />
      <span className="truncate">
        {source.name}
        {!isSite && source.page != null && ` (p. ${source.page})`}
      </span>
    </>
  )

  // A source without a url must never be an anchor (an empty href reloads the page).
  if (!href) {
    return <span className={clsx(ROW_CLASSES, 'text-muted-foreground')}>{content}</span>
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => {
        trackClick()

        // le sorgenti `site` puntano a un sito esterno, non a un upload: si aprono direttamente
        if (isSite) return

        // ! l'url memorizzato è nudo e da solo vale 403: si scambia con uno firmato al click
        event.preventDefault()
        void openSource(source, source.page != null ? `#page=${source.page}` : '')
      }}
      className={clsx(
        ROW_CLASSES,
        'min-h-[24px] py-1 text-foreground transition-colors hover:text-primary hover:underline',
      )}
    >
      {content}
    </a>
  )
}
